import { useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { IGetRowsParams, IDatasource } from "ag-grid-community";
import { useAccommodationRequestSearchFilters } from "../../hooks/use-accommodation-request-search-filters";
import { useDebouncedValue } from "@hooks";
import {
  BookingKitQueryInput,
  useAccommodationRequestSearchLazyQuery,
  useAccommodationRequestSearchTotalLazyQuery
} from "@bonliva-crm/graphql";
import { AccommodationRequestSortBy, SortOrder } from "@bonliva-crm/shared";

type Props = {
  getGridRef: () => AgGridReact;
};

export function useAccommodationRequestTableDatasource(props: Props) {
  const { getGridRef } = props;

  const {
    queryInput,
    filterManager: {
      formMethods: { watch }
    }
  } = useAccommodationRequestSearchFilters();

  const debouncedInput = useDebouncedValue(queryInput, 400);

  const queryInputRef = useRef<BookingKitQueryInput>();

  const [getItems] = useAccommodationRequestSearchLazyQuery();
  const [getTotal] = useAccommodationRequestSearchTotalLazyQuery();

  // const sortValue = watch("sort");

  useEffect(() => {
    queryInputRef.current = queryInput;
  }, [queryInput]);

  const sortValue = watch("sort");

  const getSort = () => {
    switch (sortValue) {
      case "createdOn_asc":
        return { sortBy: AccommodationRequestSortBy.CreatedOn, sortOrder: SortOrder.Asc };
      case "createdOn_desc":
        return { sortBy: AccommodationRequestSortBy.CreatedOn, sortOrder: SortOrder.Desc };
      case "arrivalDate_asc":
        return { sortBy: AccommodationRequestSortBy.FirstArrivalDate, sortOrder: SortOrder.Asc };
      case "arrivalDate_desc":
        return { sortBy: AccommodationRequestSortBy.FirstArrivalDate, sortOrder: SortOrder.Desc };

      default:
        console.log("Unknown sort value", sortValue);
    }
  };

  const getRows = (rowParams: IGetRowsParams) => {
    const filter = queryInputRef.current;
    // const filter = {};

    const limit = rowParams.endRow - rowParams.startRow;
    const skip = rowParams.startRow;

    Promise.all([
      getItems({
        variables: {
          limit,
          skip,
          filter,
          ...getSort()
        },
        fetchPolicy: "no-cache"
      }),
      getTotal({
        variables: { filter },
        fetchPolicy: "no-cache"
      })
    ])
      .then((res) => {
        rowParams.successCallback(
          res[0].data.accommodationRequestSearch.items,
          res[1].data.accommodationRequestSearchTotal.total
        );
      })
      .catch((err) => {
        console.error(err);
        rowParams.failCallback();
      });
  };

  const dataSource: IDatasource = useMemo(() => {
    return {
      rowCount: undefined,
      getRows,
      destroy: () => {
        const grid = getGridRef();
        if (!grid?.api?.isDestroyed()) {
          grid?.api.getSelectedNodes()?.forEach((node) => {
            node.setSelected(false);
          });
        }
      }
    };
  }, [debouncedInput]);

  return dataSource;
}
